import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import Logo from './assets/logo.png';

const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, checkAuthStatus } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginError, setLoginError] = useState('');

  // Redirect on auth change
  useEffect(() => {
    if (process.env.REACT_APP_SKIP_AUTH === 'true') {
      navigate('/studio/tools', { replace: true });
      return;
    }
    if (!isLoading && isAuthenticated) {
      navigate('/studio/tools', { replace: true });
    }
  }, [isLoading, isAuthenticated, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    setIsSubmitting(true);
    if (!username || !password) {
      setLoginError('Username and password are required.');
      setIsSubmitting(false);
      return;
    }
    try {
      const { authService } = await import('./services/authService');
      const result = await authService.signIn({ username, password });
      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setLoginError('A new password is required. Please reset your password.');
        setIsSubmitting(false);
        return;
      }
      if (result.isSignedIn) {
        const ok = await checkAuthStatus();
        if (ok) {
          navigate('/studio/tools', { replace: true });
          return;
        } else {
          setLoginError('Failed to update authentication state. Please try again.');
        }
      }
    } catch (err: any) {
      setLoginError(err.message || 'Login failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Logo */}
      <div className="absolute top-6 left-6 md:static md:mt-8 md:ml-12">
        <img src={Logo} alt="Agentic Genie Logo" className="w-40 md:w-48" />
      </div>
      
      {/* Centered Login Content */}
      <div className="flex flex-1 flex-col justify-center items-center px-4 pt-20 md:pt-0">
        <div className="w-full flex justify-center items-center">
          <div className="bg-white rounded-2xl shadow-2xl px-6 md:px-10 py-8 min-w-[90vw] max-w-[400px] w-full md:min-w-[350px]">
            <h3 className="text-2xl font-bold text-center text-blue-700 mb-1">Welcome</h3>
            <p className="text-center text-gray-700 mb-6">Sign in to your account</p>
            {loginError && (
              <div className="bg-red-50 text-red-500 p-3 rounded-md text-sm mb-2 text-center">
                {loginError}
              </div>
            )}
            <form className="space-y-4" onSubmit={handleLogin}>
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700">Username</label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  required
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-base bg-gray-50"
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">Password</label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-base bg-gray-50"
                  disabled={isSubmitting}
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-2 px-4 rounded-md text-white font-semibold text-lg bg-gradient-to-r from-blue-700 to-indigo-500 shadow-md hover:from-blue-800 hover:to-indigo-600 transition ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isSubmitting ? 'Signing in...' : 'Sign in'}
              </button>
            </form>
            <a href="/forgot-password" className="block text-center text-blue-600 text-sm mt-3 hover:underline">Forgot your password?</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
